import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Users,
  Gem,
  Activity,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Shield,
  Edit3,
  Trash2,
  Calendar,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import { getSocket, socketManager } from '../../services/socketService';
import UserAvatar from '../Common/UserAvatar';
import UserEditModal from './UserEditModal';
import UserCreateModal from './UserCreateModal';
import UserRoleModal from './UserRoleModal';
import UserDeleteConfirmModal from './UserDeleteConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const PAGE_SIZE = 10;

const DEFAULT_STATS = {
  totalMembers: 0,
  plusMembers: 0,
  onlineMembers: 0,
  newRegistrationsToday: 0,
  activeMembers: 0,
};

const showToast = (title, type = 'success') => {
  window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
    detail: { type, title },
  }));
};

const StatCard = ({ title, value, subtitle, icon: Icon, glowColor, valueColor = 'text-white', locale = 'tr' }) => (
  <motion.div
    whileHover={{ y: -2 }}
    className="group relative overflow-hidden rounded-xl border border-white/[0.06] bg-[#161f31] p-5 transition-all duration-300 hover:border-white/[0.12] hover:shadow-lg"
    style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.02), 0 8px 32px ${glowColor}15` }}
  >
    <div
      className="absolute -right-6 -top-6 h-28 w-28 rounded-full blur-2xl transition-opacity group-hover:opacity-100 opacity-60"
      style={{ backgroundColor: `${glowColor}18` }}
    />
    <div className="relative flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-[#7a96b0]">{title}</p>
        <p className={`mt-2 text-3xl font-bold tabular-nums ${valueColor}`}>
          {typeof value === 'number' ? value.toLocaleString(locale) : value}
        </p>
        {subtitle && (
          <div className="mt-1.5 flex items-center gap-1 text-[11px] text-[#4d6a8a]">
            <Calendar className="h-3 w-3" />
            <span>{subtitle}</span>
          </div>
        )}
      </div>
      <div
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border"
        style={{
          backgroundColor: `${glowColor}12`,
          borderColor: `${glowColor}25`,
          boxShadow: `0 0 20px ${glowColor}20`,
        }}
      >
        <Icon className="h-5 w-5" style={{ color: glowColor }} />
      </div>
    </div>
  </motion.div>
);

const UserTable = () => {
  const { t, i18n } = useTranslation('admin');
  const dateLocale = getDateFnsLocale(i18n.language);
  const { authToken, user: currentUser } = useAuth();
  const {
    users,
    filters,
    setUsers,
    setFilters,
    removeUser,
    updateUserInList,
    loading,
    setLoading,
  } = useAdminStore();

  const [stats, setStats] = useState(DEFAULT_STATS);
  const [initialLoading, setInitialLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [editingUser, setEditingUser] = useState(null);
  const [roleEditingUser, setRoleEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);

  const menuRef = useRef(null);
  const statusFilterRef = useRef(null);
  const lastNetworkErrorRef = useRef(null);
  const networkErrorShownRef = useRef(false);

  const loadUsers = useCallback(async (silent = false) => {
    if (!silent) setInitialLoading(true);
    setLoading(true);

    try {
      const result = await adminApi.getUsers({
        token: authToken,
        userId: currentUser?.id,
      });

      if (result.success) {
        setUsers(result.users || []);
        if (result.stats) {
          setStats(result.stats);
        } else {
          const list = result.users || [];
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          setStats({
            totalMembers: list.length,
            plusMembers: list.filter((u) => u.role === 'plus').length,
            onlineMembers: list.filter((u) => u.isOnline).length,
            newRegistrationsToday: list.filter(
              (u) => u.createdAt && new Date(u.createdAt) >= todayStart
            ).length,
            activeMembers: list.filter((u) => u.status === 'active').length,
          });
        }
        lastNetworkErrorRef.current = null;
        networkErrorShownRef.current = false;
      } else if (!silent) {
        showToast(result.message || t('userTable.loadFailedFallback'), 'error');
      }
    } catch (error) {
      if (silent) return;
      console.error('Admin kullanıcıları yükleme hatası:', error);
      if (!networkErrorShownRef.current) {
        showToast(getApiErrorMessage(error, { fallback: t('userTable.loadErrorFallback') }), 'error');
        networkErrorShownRef.current = true;
        lastNetworkErrorRef.current = Date.now();
      }
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [authToken, currentUser?.id, setLoading, setUsers]);

  useEffect(() => {
    if (currentUser?.id) {
      loadUsers();
    }
  }, [currentUser?.id, loadUsers]);

  // Gerçek zamanlı çevrimiçi durum — admin socket odası
  useEffect(() => {
    if (!authToken) return undefined;

    let socket = getSocket();
    if (!socket) {
      socketManager.init(authToken).catch(() => {});
      socket = getSocket();
    }
    if (!socket) return undefined;

    const handleAdminUserStatus = (data) => {
      const userId = data.user_id || data.userId;
      const isOnline = data.is_online ?? data.isOnline;

      if (userId !== undefined) {
        updateUserInList(userId, {
          isOnline: !!isOnline,
          lastSeen: data.last_seen || data.lastSeen || null,
        });
      }

      if (typeof data.online_count === 'number') {
        setStats((prev) => ({ ...prev, onlineMembers: data.online_count }));
      }
    };

    socket.on('admin:user-status', handleAdminUserStatus);
    return () => {
      socket.off('admin:user-status', handleAdminUserStatus);
    };
  }, [authToken, updateUserInList]);

  // Menü dışına tıklama + ESC
  useEffect(() => {
    if (!openMenuUserId) return undefined;

    const handlePointer = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (event.target?.closest('[data-action-menu-trigger]')) return;
      setOpenMenuUserId(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenMenuUserId(null);
        setStatusFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openMenuUserId]);

  useEffect(() => {
    if (!statusFilterOpen) return undefined;

    const handlePointer = (event) => {
      if (statusFilterRef.current?.contains(event.target)) return;
      setStatusFilterOpen(false);
    };

    document.addEventListener('mousedown', handlePointer);
    return () => document.removeEventListener('mousedown', handlePointer);
  }, [statusFilterOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.userSearch, filters.userStatus]);

  const filteredUsers = useMemo(() => {
    const query = (filters.userSearch || '').trim().toLowerCase();
    return users.filter((u) => {
      const matchesSearch =
        !query ||
        u.name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query) ||
        u.username?.toLowerCase().includes(query) ||
        String(u.id).toLowerCase().includes(query);

      const matchesStatus =
        filters.userStatus === 'all' || u.status === filters.userStatus;

      return matchesSearch && matchesStatus;
    });
  }, [users, filters.userSearch, filters.userStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const formatDate = (dateString) => {
    if (!dateString) return t('userTable.neverDate');
    try {
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: dateLocale });
    } catch {
      return t('userTable.neverDate');
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="inline-flex items-center rounded-full bg-red-500 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
          {t('userTable.roleBadges.admin')}
        </span>
      );
    }
    if (role === 'plus') {
      return (
        <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium text-amber-400">
          {t('userTable.roleBadges.plus')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-0.5 text-[11px] font-medium text-[#7a96b0]">
        {t('userTable.roleBadges.free')}
      </span>
    );
  };

  const getOnlineBadge = (isOnline) => {
    if (isOnline) {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-400">
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
          {t('userTable.online')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4d6a8a]">
        <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[#4d6a8a]" />
        {t('userTable.offline')}
      </span>
    );
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    setActionLoading(true);
    try {
      const result = await adminApi.deleteUser({
        token: authToken,
        userId: deletingUser.id,
      });
      if (result.success) {
        removeUser(deletingUser.id);
        setStats((prev) => ({
          ...prev,
          totalMembers: Math.max(0, prev.totalMembers - 1),
          plusMembers: deletingUser.role === 'plus'
            ? Math.max(0, prev.plusMembers - 1)
            : prev.plusMembers,
          onlineMembers: deletingUser.isOnline
            ? Math.max(0, prev.onlineMembers - 1)
            : prev.onlineMembers,
          activeMembers: deletingUser.status === 'active'
            ? Math.max(0, prev.activeMembers - 1)
            : prev.activeMembers,
        }));
        setDeletingUser(null);
        showToast(t('userTable.deletedSuccess'));
      } else {
        showToast(result.error || t('userTable.deleteFailedFallback'), 'error');
      }
    } catch (error) {
      showToast(getApiErrorMessage(error, { fallback: t('userTable.deleteErrorFallback') }), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openActionMenu = (event, userId) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 6,
      left: Math.max(8, rect.right - 192),
    });
    setOpenMenuUserId((prev) => (prev === userId ? null : userId));
  };

  const handleMenuAction = (action, user) => {
    setOpenMenuUserId(null);
    if (action === 'role') setRoleEditingUser(user);
    else if (action === 'edit') setEditingUser(user);
    else if (action === 'delete') setDeletingUser(user);
  };

  const statusFilterLabel =
    filters.userStatus === 'active'
      ? t('userTable.statusFilter.active')
      : filters.userStatus === 'inactive'
        ? t('userTable.statusFilter.inactive')
        : t('userTable.statusFilter.all');

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);

    for (let i = start; i <= end; i += 1) pages.push(i);
    return pages;
  }, [safePage, totalPages]);

  if (initialLoading && users.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-3 text-[#7a96b0]">
          <Loader2 className="h-5 w-5 animate-spin text-[#3d8ef0]" />
          <span>{t('userTable.loadingUsers')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Üst başlık + arama */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="text-xl font-bold text-white">{t('userTable.title')}</h2>
        <div className="relative w-full lg:max-w-md lg:flex-1 lg:mx-8">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4d6a8a]" />
          <input
            type="text"
            placeholder={t('userTable.searchPlaceholder')}
            value={filters.userSearch}
            onChange={(e) => setFilters({ userSearch: e.target.value })}
            className="w-full rounded-xl border border-white/[0.08] bg-[#161f31] py-2.5 pl-10 pr-4 text-sm text-white placeholder-[#4d6a8a] transition-colors focus:border-[#3d8ef0]/40 focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/20"
          />
        </div>
        <div className="hidden w-24 lg:block" aria-hidden="true" />
      </div>

      {/* Alt başlık + filtreler */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-2xl font-bold text-white">{t('userTable.membersTitle')}</h3>
          <p className="mt-1 text-sm text-[#7a96b0]">
            {t('userTable.activeUsersCount', { count: stats.activeMembers.toLocaleString(i18n.language) })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative" ref={statusFilterRef}>
            <button
              type="button"
              onClick={() => setStatusFilterOpen((v) => !v)}
              className="flex items-center gap-2 rounded-xl border border-white/[0.08] bg-[#161f31] px-4 py-2.5 text-sm text-[#c8d8eb] transition-colors hover:bg-[#1e2d47]"
            >
              <span>{statusFilterLabel}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${statusFilterOpen ? 'rotate-180' : ''}`} />
            </button>
            {statusFilterOpen && (
              <div className="absolute right-0 top-full z-20 mt-1.5 min-w-[160px] overflow-hidden rounded-xl border border-white/[0.08] bg-[#0d1424] py-1 shadow-[0_16px_48px_rgba(0,0,0,0.5)]">
                {[
                  { value: 'all', label: t('userTable.statusFilter.all') },
                  { value: 'active', label: t('userTable.statusFilter.active') },
                  { value: 'inactive', label: t('userTable.statusFilter.inactive') },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setFilters({ userStatus: opt.value });
                      setStatusFilterOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors hover:bg-white/[0.05] ${
                      filters.userStatus === opt.value ? 'text-[#3d8ef0]' : 'text-[#c8d8eb]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-xl bg-[#3d8ef0] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_0_24px_rgba(61,142,240,0.35)] transition-colors hover:bg-[#5ca3f5]"
          >
            <UserPlus className="h-4 w-4" />
            <span>{t('userTable.newUser')}</span>
          </motion.button>
        </div>
      </div>

      {/* İstatistik kartları */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title={t('userTable.statCards.totalMembers')}
          value={stats.totalMembers}
          icon={Users}
          glowColor="#3d8ef0"
          locale={i18n.language}
        />
        <StatCard
          title={t('userTable.statCards.plusMembers')}
          value={stats.plusMembers}
          icon={Gem}
          glowColor="#f59e0b"
          valueColor="text-amber-400"
          locale={i18n.language}
        />
        <StatCard
          title={t('userTable.statCards.onlineMembers')}
          value={stats.onlineMembers}
          icon={Activity}
          glowColor="#10b981"
          valueColor="text-emerald-400"
          locale={i18n.language}
        />
        <StatCard
          title={t('userTable.statCards.newRegistrations')}
          value={stats.newRegistrationsToday}
          subtitle={t('userTable.statCards.today')}
          icon={UserPlus}
          glowColor="#6366f1"
          locale={i18n.language}
        />
      </div>

      {/* Tablo */}
      <div className="overflow-hidden rounded-xl border border-white/[0.06] bg-[#0d1424]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-white/[0.06] bg-[#161f31]/50">
                {[
                  t('userTable.columns.user'),
                  t('userTable.columns.email'),
                  t('userTable.columns.role'),
                  t('userTable.columns.status'),
                  t('userTable.columns.lastLogin'),
                  t('userTable.columns.note'),
                  t('userTable.columns.action'),
                ].map((col) => (
                  <th
                    key={col}
                    className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#4d6a8a]"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {loading && paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#3d8ef0]" />
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-[#7a96b0]">
                    {t('userTable.noUsersFound')}
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((u) => (
                  <motion.tr
                    key={u.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="transition-colors hover:bg-white/[0.02]"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <UserAvatar
                          userId={u.id}
                          avatarUrl={u.avatarUrl}
                          name={u.name}
                          size="md"
                          className="rounded-lg"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{u.name}</p>
                          <p className="truncate text-xs text-[#4d6a8a]">
                            ID: {String(u.id).substring(0, 8)}...
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-[#8aaccc]">{u.email}</span>
                    </td>
                    <td className="px-5 py-4">{getRoleBadge(u.role)}</td>
                    <td className="px-5 py-4">{getOnlineBadge(u.isOnline)}</td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-[#c8d8eb]">{formatDate(u.lastLogin)}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm font-semibold text-[#3d8ef0]">
                        {u.noteCount ?? 0}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        data-action-menu-trigger
                        onClick={(e) => openActionMenu(e, u.id)}
                        className="rounded-lg p-2 text-[#4d6a8a] transition-colors hover:bg-white/[0.05] hover:text-white"
                        aria-label={t('userTable.actionMenu')}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-white/[0.06] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-[#7a96b0]">
              {t('userTable.pageOf', { current: safePage, total: totalPages })}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.08] bg-[#161f31] text-[#c8d8eb] transition-colors hover:bg-[#1e2d47] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {pageNumbers[0] > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    className="flex h-9 min-w-[36px] items-center justify-center rounded-lg px-2 text-sm text-[#c8d8eb] hover:bg-white/[0.05]"
                  >
                    1
                  </button>
                  {pageNumbers[0] > 2 && (
                    <span className="px-1 text-[#4d6a8a]">...</span>
                  )}
                </>
              )}

              {pageNumbers.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`flex h-9 min-w-[36px] items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors ${
                    page === safePage
                      ? 'bg-[#3d8ef0] text-white shadow-[0_0_16px_rgba(61,142,240,0.4)]'
                      : 'text-[#c8d8eb] hover:bg-white/[0.05]'
                  }`}
                >
                  {page}
                </button>
              ))}

              {pageNumbers[pageNumbers.length - 1] < totalPages && (
                <>
                  {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                    <span className="px-1 text-[#4d6a8a]">...</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    className="flex h-9 min-w-[36px] items-center justify-center rounded-lg px-2 text-sm text-[#c8d8eb] hover:bg-white/[0.05]"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.08] bg-[#161f31] text-[#c8d8eb] transition-colors hover:bg-[#1e2d47] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Aksiyon menüsü dropdown */}
      {openMenuUserId && (
        <div
          ref={menuRef}
          className="fixed z-[100010] min-w-[192px] overflow-hidden rounded-xl border border-white/[0.08] bg-[#0d1424] py-1 shadow-[0_16px_48px_rgba(0,0,0,0.6)]"
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          {(() => {
            const targetUser = users.find((u) => u.id === openMenuUserId);
            if (!targetUser) return null;
            return (
              <>
                <button
                  type="button"
                  onClick={() => handleMenuAction('role', targetUser)}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#c8d8eb] transition-colors hover:bg-white/[0.05]"
                >
                  <Shield className="h-4 w-4 text-[#3d8ef0]" />
                  {t('userTable.menu.editRole')}
                </button>
                <button
                  type="button"
                  onClick={() => handleMenuAction('edit', targetUser)}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#c8d8eb] transition-colors hover:bg-white/[0.05]"
                >
                  <Edit3 className="h-4 w-4 text-[#8aaccc]" />
                  {t('userTable.menu.editInfo')}
                </button>
                <div className="my-1 border-t border-white/[0.06]" />
                <button
                  type="button"
                  onClick={() => handleMenuAction('delete', targetUser)}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-red-400 transition-colors hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4" />
                  {t('userTable.menu.deleteUser')}
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Modals */}
      {editingUser && (
        <UserEditModal
          user={editingUser}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            loadUsers(true);
            showToast(t('userTable.updatedSuccess'));
          }}
        />
      )}
      {roleEditingUser && (
        <UserRoleModal
          user={roleEditingUser}
          isOpen={!!roleEditingUser}
          onClose={() => setRoleEditingUser(null)}
          onSuccess={() => loadUsers(true)}
        />
      )}
      {showCreateModal && (
        <UserCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadUsers(true);
            showToast(t('userTable.createdSuccess'));
          }}
        />
      )}
      {deletingUser && (
        <UserDeleteConfirmModal
          user={deletingUser}
          isOpen={!!deletingUser}
          onClose={() => setDeletingUser(null)}
          onConfirm={handleDelete}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default UserTable;
